import { action, observable, computed } from "mobx";
import { Toast } from "Tools/Toast";
import Stores from "..";
import { IEntities, TEntity } from "./types";
import { IAuthStore } from "../Auth/types";
import { IManagementStore } from "../Management/types";
import { IValuesStore } from "../Values/types";

import { Location, LocationShort } from "@src/Interfaces/Entity/Location";
import { Person } from "@src/Interfaces/Entity/Person";
import { Position } from "@src/Interfaces/Entity/Position";
import { Organisation } from "@src/Interfaces/Entity/Organisation";
import { Permission } from "@src/Interfaces/Entity/Permission";
import { PermissionGroup } from "@src/Interfaces/Entity/PermissionGroup";
import { Theme } from "@src/Interfaces/Entity/Theme";
import { ApplicationLink } from "@src/Interfaces/Entity/ApplicationLink";
import { PositionTeam } from "@src/Interfaces/Entity/PositionTeam";

class SearchStore implements SearchStore {
  @observable searchString: string = "";
  @observable entitiesLoaded: boolean = false;
  @observable entities: IEntities;
  @observable filteredEntities: IEntities;
  @observable searchFieldOpened: boolean = false;

  @computed
  get globalSearchActive() {
    return this.searchString.length > 0 && this.entitiesLoaded;
  }

  @action.bound
  toggleSearch = () => {
    this.searchFieldOpened = !this.searchFieldOpened;
  };

  @action.bound
  setSearchString(str: string) {
    this.searchString = str;
  }

  @action.bound
  async loadAllEntities() {
    try {
      const AccessAuth: IAuthStore = Stores.auth;
      const AccessManagement: IManagementStore = Stores.management;
      const AccessValues: IValuesStore = Stores.values;

      const userRoles = AccessAuth.user.roles;
      const masterPossibility =
        userRoles.includes(AccessValues.roles.Administrator);

      const requestsArray: Array<Promise<
        | TEntity[]
        | LocationShort[]
        | Organisation[]
        | PermissionGroup[]
        | Permission[]
        | Position[]
        | Person[]
        | Theme[]
        | ApplicationLink[]
      >> = [];

      AccessValues.forceRequestProcess(true);
      if (userRoles.includes(AccessValues.roles.Manager) || masterPossibility) {
          requestsArray.push(AccessManagement.getLocations());
          requestsArray.push(AccessManagement.getOrganisations());
          requestsArray.push(AccessManagement.getPermissions());
          requestsArray.push(AccessManagement.getPermissionGroups());
          requestsArray.push(AccessManagement.getPositions());
          requestsArray.push(AccessManagement.getPositionTeams());
          requestsArray.push(AccessManagement.getPersons());
          requestsArray.push(AccessManagement.getThemes());
          requestsArray.push(AccessManagement.getApplicationLinks());
      } else {
        requestsArray.push(null);
      }

      const [
        locations,
        organisations,
        permissions,
        permissionGroups,
        positions,
        positionTeams,
        persons,
        themes,
        applicationLinks,
      ] = await Promise.all(requestsArray);

      AccessValues.forceRequestProcess(false);

      this.entities = {
        locations: locations as Location[],
        organisations: organisations as Organisation[],
        permissions: permissions as Permission[],
        permissionGroups: permissionGroups as PermissionGroup[],
        positions: positions as Position[],
        positionTeams: positionTeams as PositionTeam[],
        persons: persons as Person[],
        themes: themes as Theme[],
        applicationLinks: applicationLinks as ApplicationLink[],
      };

      this.filteredEntities = this.entities;

      this.entitiesLoaded = true;

      return this.entities;
    } catch (error) {
      console.error(error);
    }
  }

  @action.bound
  filterByAllEntities() {
    const keyWord: string = this.searchString.toLowerCase();

    const AccessValues = Stores.values;
    const AccessManagement = Stores.management;

    this.filteredEntities = { ...this.entities };

    Object.keys(this.filteredEntities).forEach((entKey: string) => {
      this.filteredEntities[entKey] = AccessManagement[entKey].filter((entity: []) => {
        return Object.keys(entity).some((entityFieldKey: string) => {
          const entityFieldValue = entity[entityFieldKey];

          if (typeof entityFieldValue === "string") {
            return Boolean(entityFieldValue.toLowerCase().match(keyWord));
          } else if (entityFieldKey === "aliasNames") {
            return entityFieldValue.find(
              (alias: any) =>
                Boolean(alias.name.toLowerCase().match(keyWord)) || Boolean(alias.fullName.toLowerCase().match(keyWord))
            );
          } else if (entityFieldKey === "type") {
            const locations = Object.entries(AccessValues.locationTypes).filter(
              ([locKey, locValue]: [string, number]) => {
                return Boolean(String(locKey).match(keyWord));
              }
            );

            return locations.some(([locKey, locValue]: [string, number]) => locValue === entityFieldValue);
          }
        });
      });
    });

    return this.filteredEntities;
  }

  @action.bound
  updateFilteredEntity(id: string, entKey: keyof IEntities) {
    if (!this.filteredEntities) {
      return;
    }
    const filteredEntityIndex: number = (this.filteredEntities[entKey] as TEntity[]).findIndex(
      (ent: TEntity) => ent.id === id
    );

    const commonEntity: TEntity = (Stores.management[entKey] as TEntity[]).find((ent: TEntity) => ent.id === id);

    if (filteredEntityIndex >= 0 && commonEntity) {
      this.filteredEntities[entKey][filteredEntityIndex] = commonEntity;
    }
  }
}

export default SearchStore;
